import React, { useState } from 'react';
import { HighlightedText } from '../common/HighlightedText';
import { speak, isSpeechSupported } from '../../utils/speak';

export const WordItem = ({ word, onEdit, onDelete, onUpdateLearningLevel, onWordClick }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const learningLevel = word.learningLevel || 'learning';

  const levelConfig = {
    mastered: {
      label: 'Mastered',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      hoverColor: 'hover:bg-green-200',
      borderColor: 'border-green-300',
    },
    learning: {
      label: 'Learning',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      hoverColor: 'hover:bg-blue-200',
      borderColor: 'border-blue-300',
    },
    hard: {
      label: 'Hard',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-800',
      hoverColor: 'hover:bg-orange-200',
      borderColor: 'border-orange-300',
    },
  };

  const handleLevelChange = async (newLevel) => {
    if (newLevel === learningLevel || isUpdating) return;

    setIsUpdating(true);
    try {
      await onUpdateLearningLevel(word.wordId, newLevel);
    } catch (err) {
      console.error('Failed to update learning level:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const currentConfig = levelConfig[learningLevel];

  return (
    <div className="border-b border-gray-200 py-4 px-4 sm:px-6 hover:bg-gray-50 transition-colors overflow-hidden">
      <div className="flex items-start gap-2 sm:gap-4">
        {/* Main content area */}
        <div className="flex-1 min-w-0">
          {/* Word and translation row */}
          <div className="mb-2">
            <div className="flex items-center gap-2 mb-1">
              <h3
                className="text-lg font-semibold text-gray-900 truncate max-w-[200px] sm:max-w-[300px] cursor-pointer hover:text-primary-600 transition-colors"
                title={word.word}
                onClick={() => onWordClick && onWordClick(word)}
              >
                {word.word}
              </h3>
              {isSpeechSupported() && (
                <button
                  onClick={(e) => { e.stopPropagation(); speak(word.word); }}
                  className="flex-shrink-0 text-gray-400 hover:text-primary-600 transition-colors"
                  title="Pronounce"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6a7 7 0 010 12M8 9l3-3v12l-3-3H5a1 1 0 01-1-1v-4a1 1 0 011-1h3z" />
                  </svg>
                </button>
              )}
              {word.translation && (
                <span className="text-base font-normal text-gray-500 italic truncate max-w-[150px] sm:max-w-[250px]" title={word.translation.text}>
                  ({word.translation.text})
                </span>
              )}
            </div>

            {/* Quick Level Change Buttons */}
            <div className="flex gap-2 flex-wrap">
              {Object.entries(levelConfig).map(([level, config]) => (
                <button
                  key={level}
                  onClick={() => handleLevelChange(level)}
                  disabled={isUpdating}
                  className={`
                    px-2 sm:px-3 py-1 text-xs font-medium rounded-md border transition-all
                    ${
                      learningLevel === level
                        ? `${config.bgColor} ${config.textColor} ${config.borderColor}`
                        : `bg-white text-gray-600 border-gray-300 ${config.hoverColor}`
                    }
                    ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  title={`Mark as ${config.label}`}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>

          {/* Examples */}
          <div className="space-y-2 ml-1">
            {word.examples.map((example, index) => (
              <div key={index} className="flex gap-2 sm:gap-3 items-start">
                <span className="text-primary-500 font-bold text-xl leading-none flex-shrink-0">•</span>
                <p className="text-gray-700 text-sm sm:text-base break-words min-w-0">
                  <HighlightedText text={example.text} />
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons - always visible */}
        <div className="flex gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={() => onEdit(word)}
            className="p-1.5 sm:p-2 text-primary-600 hover:bg-primary-50 rounded transition-colors"
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(word.wordId)}
            className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
